/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { RosterManagementStudentWithGuardiansListItem } from '@whizz/api-model';
import type { AddEditParentModalProps } from './modules/AddEditParentsModal';
import type { UploadParentsModalProps } from './modules/Upload';
import type { StudentType } from '../../../types';
import React, { useState } from 'react';
import { repository } from '@whizz/api-client';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import { debounce } from 'lodash';
import { useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { Button, ChoiceGroup, Input, Selector } from '../../../../components';
import Loader from '../../../../components/loader/Loader';
import { useCleverIntegration } from '../../../../hooks/useCleverIntegration';
import { useModal } from '../../../../hooks/useModal';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import { AddEditParentsModal, ParentsTable } from './modules';
import Upload from './modules/Upload';
import Sponsorship from './modules/Sponsorship/Sponsorship';

type SearchDataType = {
  search: string | null,
  classId: number | null,
  all: boolean,
};

const Parents = (): JSX.Element => {
  const { translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const queryClient = useQueryClient();

  const [ searchData, setSearchData ] = useState<SearchDataType>({ search: null, classId: null, all: true });
  const [ viewType, setViewType ] = useState<'parents' | 'sponsorship'>('parents');

  // const [ uploadModal, toggleUploadModal, setUploadModalProps ] = useModal(
  //   (props: UploadParentsModalProps) => (
  //     <Upload
  //       {...props}
  //     />
  //   ),
  // );

  const [ parentsModal, toggleParentsModal, setParentsModalProps ] = useModal(
    (props: AddEditParentModalProps) => (
      <AddEditParentsModal
        {...props}
      />
    ),
  );

  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const onSuccess = () => {
    queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.GUARDIANS ]);
    toggleMessageType('success');
  };

  const onError = () => toggleMessageType('error');

  const studentsQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.GUARDIANS ],
    () => repository.StudentRepository.rosterManagementListItemsByAction(
      RosterManagementStudentActionEnum.guardians,
    ),
    {
      placeholderData: [],
    },
  );

  const classesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES, 'withoutArchived' ],
    () => repository.NewClassRepository.getClasses({ includeArchived: false }),
    {
      placeholderData: [],
    },
  );

  const classesOptions = classesQuery.data!.map((classItem) => ({
    label: classItem.name,
    value: classItem.id,
  }));

  const students = studentsQuery.data?.map((student) => ({ ...student, fullName: `${student.firstName} ${student.lastName}` })) as unknown as StudentType[] ?? [];

  // const handleUpload = () => {
  //   setUploadModalProps({
  //     title: translate('pages.ROSTER_MANAGEMENT.PARENTS.UPLOAD.TITLE'),
  //     disableMaskClick: true,
  //     onSuccess,
  //   });
  //   toggleUploadModal(true);
  // };

  const handleEdit = (student: RosterManagementStudentWithGuardiansListItem) => {
    setParentsModalProps({
      title: translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.TITLE', { studentName: `${student.firstName} ${student.lastName}` }),
      student: student,
      disableMaskClick: true,
      onSuccess,
      onError,
    });
    toggleParentsModal(true);
  };

  const filteredStudentsFunction = () => {
    let filteredStudents = students as unknown as RosterManagementStudentWithGuardiansListItem[];

    if (null != searchData.search) {
      filteredStudents = filteredStudents
        .filter((student: RosterManagementStudentWithGuardiansListItem) => {
          const lowerSearch = searchData.search!.toLowerCase();
          const lowerFirstName = (student.firstName ?? '').toLowerCase();
          const lowerLastName = (student.lastName ?? '').toLowerCase();

          return `${lowerFirstName} ${lowerLastName}`.includes(lowerSearch)
            || `${lowerLastName}, ${lowerFirstName}`.includes(lowerSearch);
        })
      ;
    }

    if (null != searchData.classId) {
      filteredStudents = filteredStudents.filter((student: RosterManagementStudentWithGuardiansListItem) => student.classes?.some((c) => c.id === searchData.classId));
    }

    if (!searchData.all) {
      filteredStudents = filteredStudents.filter((student: RosterManagementStudentWithGuardiansListItem) => student.guardians.length === 0);
    }

    return filteredStudents;
  };

  const filteredStudents = filteredStudentsFunction();

  const getStatistics = () => {
    const students = studentsQuery.data as unknown as RosterManagementStudentWithGuardiansListItem[] ?? [];
    const studentsWithParentEmails = students?.filter((student) => student.guardians.length > 0) ?? [];
    const totalStudents = students.length;
    const totalStudentsWithParentEmails = studentsWithParentEmails.length;
    const totalParents = studentsWithParentEmails.reduce((previousValue, currentValue) => previousValue += currentValue.guardians.length, 0);

    return { totalStudents, totalStudentsWithParentEmails, totalParents };
  };

  const Statistics = () => {
    if (!studentsQuery.isFetched || studentsQuery.isFetching) {
      return <Loader disableLabel smallSize />;
    }
    const { totalStudents, totalStudentsWithParentEmails, totalParents } = getStatistics();

    return (
      <>
        <span>{translate('pages.ROSTER_MANAGEMENT.PARENTS.STATISTICS.ACTIVE_STUDENTS', { total: totalStudents })}</span>
        <span>{translate('pages.ROSTER_MANAGEMENT.PARENTS.STATISTICS.TOTAL_STUDENTS_WITH_PARENT_EMAILS', { total: totalStudentsWithParentEmails })}</span>
        <span>{translate('pages.ROSTER_MANAGEMENT.PARENTS.STATISTICS.TOTAL_PARENTS', { total: totalParents })}</span>
      </>
    );
  };

  const setExtraDataWithDebounce = debounce(setSearchData, 500);

  const content = () => {
    switch (viewType) {
      case 'parents':
        return (
          <div className={'flex flex-col items-start space-y-4 w-full'}>
            <Button
              onClick={() => setViewType('sponsorship')}
              isOutline
              color={'positive'}
              className={'self-end'}
            >{translate('pages.ROSTER_MANAGEMENT.SPONSORSHIP.BUTTON')}</Button>
            <div className={'w-full space-y-4 mb-6'}>
              <div className={'flex justify-between'}>
                <div className={'space-y-4 w-full md:w-2/3 '}>
                  <ChoiceGroup id={'all-parents'} checked={!searchData.all} onChange={() => setSearchData((prev) => ({ ...prev, all: !prev.all }))} label={translate('pages.ROSTER_MANAGEMENT.PARENTS.BUTTONS.FILTER')} />
                  <div className={'flex w-full space-x-4'}>
                    <div className={'w-full'}>
                      <StudentSearch
                        id={'search_students'}
                        key={'search_students'}
                        label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SEARCH')}
                        placeholder={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SEARCH_STUDENT')}
                        icon={GLOBAL_ICONS.search}
                        iconPosition={'left'}
                        onChange={(event) => setExtraDataWithDebounce((prev) => ({ ...prev, search: event.target.value }))}
                        onClear={() => setExtraDataWithDebounce((prev) => ({ ...prev, search: '' }))}
                        hideLabel
                      />
                    </div>
                    <div className={'w-full'}>
                      <Selector
                        id={'class'}
                        placeholder={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SELECT_CLASS')}
                        options={classesOptions}
                        value={classesOptions.find((c) => c.value === searchData.classId)}
                        hideLabel
                        onChange={(selectedOption: any) => setSearchData((prev) => ({ ...prev, classId: selectedOption?.value ?? null }))}
                      />
                    </div>
                  </div>
                </div>
                {/* <Button
                  icon={'Publish'}
                  onClick={handleUpload}
                  className={'justify-self-end self-start'}
                  data-cy={'rm-new-parents-bulk-upload'}
                  disabled={isClever}
                  isOutline
                  color={'positive'}
                >
                  {translate('pages.ROSTER_MANAGEMENT.PARENTS.BUTTONS.UPLOAD')}
                </Button> */}
              </div>
              <ParentsTable
                isLoading={studentsQuery.isFetching}
                students={filteredStudents}
                onEdit={handleEdit}
              />
            </div>
          </div>
        );
      case 'sponsorship':
        return (
          <div className={'space-y-4 w-full'}>
            <Button
              onClick={() => setViewType('parents')}
              isOutline
              color={'positive'}
              icon='ChevronLeft'
            >Back</Button>
            <Sponsorship totalParents={getStatistics().totalParents} />
          </div>
        );
    }
  };

  return (
    <RosterManagementLayout
      title={translate(`pages.ROSTER_MANAGEMENT.${viewType.toUpperCase()}.TITLE`)}
      statistics={<Statistics />}
    >

      {parentsModal}
      {successErrorModal}
      {content()}
    </RosterManagementLayout>
  );
};

const StudentSearch = styled(Input)`
  border: 2px solid black;
  border-radius: 8px;
  background: #fff;
  height: 45px;
`;

export default Parents;
